import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { hitApi, dateFormatter } from "../../components/commonAction";
import AffiliatePageLayout from "./AffiliatePageLayout";

export default function Dashboard({ pageTitle }) {
    const [data, setData]                           = useState(null);
    const [loading, setLoading]                     = useState(true);
    const [analytics, setAnalytics]                 = useState(null);
    const [recentReferrals, setRecentReferrals]     = useState([]);
    const [recentCommissions, setRecentCommissions] = useState([]);

    useEffect(() => {
        hitApi({ url: "affiliate/dashboard",         method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setData(resp.data || null);
        });
        hitApi({ url: "affiliate/analytics?range=30", method: "GET", data: {}, alert: false }, (resp) => {
            if (resp?.status) setAnalytics(resp.data || null);
        });
        hitApi({ url: "affiliate/referrals?limit=5", method: "GET", data: {}, alert: false }, (resp) => {
            if (resp?.status) setRecentReferrals(resp.data?.referrals || []);
        });
        hitApi({ url: "affiliate/commissions",       method: "GET", data: {}, alert: false }, (resp) => {
            if (resp?.status) setRecentCommissions((resp.data?.commissions || []).slice(0, 3));
        });
    }, []);

    const aff        = data?.affiliate;
    const stats      = data?.stats || {};
    const isApproved = aff?.status === "approved";
    const allZero    = !Number(stats.lifetimeEarnings) && !Number(stats.totalClicks) && !Number(stats.totalConversions);

    return (
        <AffiliatePageLayout pageTitle={pageTitle} heading="Affiliate Dashboard">
            {loading ? (
                <DashboardSkeleton />
            ) : (
                <>
                    {!aff && (
                        <div className="pxl-status-banner pxl-status-banner--warning">
                            You're not yet registered as an affiliate.
                            <Link to="/affiliate/signup" className="ms-1">Apply here</Link>.
                        </div>
                    )}
                    {aff?.status === "pending" && (
                        <div className="pxl-status-banner pxl-status-banner--info">
                            Your application is awaiting admin approval. You'll receive an email when it's processed.
                        </div>
                    )}
                    {aff?.status === "suspended" && (
                        <div className="pxl-status-banner pxl-status-banner--danger">
                            Your affiliate account is suspended. Please contact support.
                        </div>
                    )}

                    <div className="pxl-stat-grid">
                        <StatCard label="Lifetime Earnings"  value={fmtMoney(stats.lifetimeEarnings)}                              icon={<IconTrendingUp />}   money />
                        <StatCard label="Available Balance"  value={fmtMoney(stats.currentBalance)}   negative={Number(stats.currentBalance) < 0} icon={<IconCreditCard />}  money />
                        <StatCard label="Lifetime Payouts"   value={fmtMoney(stats.totalPaidOut)}                                  icon={<IconSend />}         money />
                        <StatCard label="Total Conversions"  value={fmtNum(stats.totalConversions)}                                icon={<IconUserCheck />} />
                        <StatCard label="Total Clicks"       value={fmtNum(stats.totalClicks)}                                     icon={<IconMousePointer />} />
                        <StatCard label="MTD Earnings"       value={fmtMoney(stats.mtdEarnings)}                                   icon={<IconCalendar />}     money />
                    </div>

                    {isApproved && <ReferralCodePanel code={aff.referralCode} />}

                    {isApproved && allZero && <GettingStarted />}

                    {isApproved && !allZero && analytics?.series?.daily?.length > 0 && (
                        <MiniChart daily={analytics.series.daily} />
                    )}

                    {isApproved && !allZero && (recentReferrals.length > 0 || recentCommissions.length > 0) && (
                        <div className="row g-3 mt-1">
                            {recentReferrals.length > 0 && (
                                <div className="col-md-6">
                                    <div className="pxl-panel h-100">
                                        <div className="pxl-panel-header">
                                            Recent clicks
                                            <Link to="/affiliate/referrals" className="pxl-panel-header-link">View all</Link>
                                        </div>
                                        <div className="pxl-panel-body pxl-panel-body--flush">
                                            {recentReferrals.map(r => (
                                                <div key={r._id} className="pxl-activity-row">
                                                    <div className="pxl-activity-icon pxl-activity-icon--click">
                                                        <IconMousePointer />
                                                    </div>
                                                    <div className="pxl-activity-meta">
                                                        <div className="pxl-activity-label">{r.landingPath || "/"}</div>
                                                        <div className="pxl-activity-time">{dateFormatter(r.createdAt)}</div>
                                                    </div>
                                                    {r.convertedToUserId
                                                        ? <span className="pxl-pill pxl-pill--success">converted</span>
                                                        : <span className="pxl-pill pxl-pill--muted">click</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {recentCommissions.length > 0 && (
                                <div className="col-md-6">
                                    <div className="pxl-panel h-100">
                                        <div className="pxl-panel-header">
                                            Recent commissions
                                            <Link to="/affiliate/commissions" className="pxl-panel-header-link">View all</Link>
                                        </div>
                                        <div className="pxl-panel-body pxl-panel-body--flush">
                                            {recentCommissions.map(r => (
                                                <div key={r._id} className="pxl-activity-row">
                                                    <div className={`pxl-activity-icon ${r.type === "earned" ? "pxl-activity-icon--earned" : "pxl-activity-icon--clawback"}`}>
                                                        <IconTrendingUp />
                                                    </div>
                                                    <div className="pxl-activity-meta">
                                                        <div className="pxl-activity-label">
                                                            {r.type === "earned" ? "Commission earned" : "Clawed back"}
                                                        </div>
                                                        <div className="pxl-activity-time">{dateFormatter(r.createdAt)}</div>
                                                    </div>
                                                    <div className={`pxl-activity-amount ${Number(r.amount) < 0 ? "pxl-amount-negative" : ""}`}>
                                                        {r.currency} {Number(r.amount).toFixed(2)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </AffiliatePageLayout>
    );
}

function DashboardSkeleton() {
    return (
        <>
            {/* stat cards grid — mirrors pxl-stat-grid */}
            <div className="pxl-stat-grid" style={{ marginBottom: 16 }}>
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="pxl-sk-card">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                            <span className="pxl-sk" style={{ width: '55%', height: 10 }} />
                            <span className="pxl-sk" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }} />
                        </div>
                        <span className="pxl-sk" style={{ width: '50%', height: 26 }} />
                    </div>
                ))}
            </div>

            {/* referral code panel */}
            <div className="pxl-referral-panel">
                <div>
                    <span className="pxl-sk" style={{ width: 110, height: 10, marginBottom: 10, display: 'block' }} />
                    <span className="pxl-sk" style={{ width: 160, height: 22, display: 'block' }} />
                </div>
                <span className="pxl-sk" style={{ width: 112, height: 38, borderRadius: 10, flexShrink: 0 }} />
            </div>

            {/* chart panel */}
            <div className="pxl-sk-panel">
                <div className="pxl-sk-panel-header">
                    <span className="pxl-sk" style={{ width: 170, height: 13 }} />
                </div>
                <div className="pxl-sk-panel-body">
                    <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <span className="pxl-sk" style={{ width: 60, height: 10 }} />
                        <span className="pxl-sk" style={{ width: 60, height: 10 }} />
                    </div>
                    <span className="pxl-sk" style={{ width: '100%', height: 140, borderRadius: 8, display: 'block' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                        <span className="pxl-sk" style={{ width: 40, height: 9 }} />
                        <span className="pxl-sk" style={{ width: 40, height: 9 }} />
                    </div>
                </div>
            </div>
        </>
    );
}

function StatCard({ label, value, negative, icon, money }) {
    return (
        <div className={`pxl-stat-card${money ? " pxl-stat-card--money" : ""}`}>
            <div className="pxl-stat-card-header">
                <span className="pxl-stat-card-label">{label}</span>
                <span className={`pxl-stat-card-icon${money ? " pxl-stat-card-icon--money" : ""}`}>{icon}</span>
            </div>
            <div className={`pxl-stat-card-value${negative ? " is-negative" : ""}`}>{value}</div>
        </div>
    );
}

function ReferralCodePanel({ code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="pxl-referral-panel">
            <div>
                <div className="pxl-referral-panel-label">Your referral code</div>
                <div className="pxl-referral-panel-code">{code}</div>
            </div>
            <div className="pxl-referral-panel-actions">
                <button className="pxl-copy-btn" onClick={handleCopy}>
                    {copied ? <><IconCheck /> Copied!</> : <><IconCopy /> Copy code</>}
                </button>
            </div>
        </div>
    );
}

function GettingStarted() {
    return (
        <div className="pxl-gs-panel">
            <div className="pxl-gs-header">
                <div className="pxl-gs-header-icon"><IconRocket /></div>
                <div className="pxl-gs-header-text">
                    <div className="pxl-gs-title">Start earning commissions</div>
                    <div className="pxl-gs-subtitle">
                        Your affiliate account is active. Follow these steps to make your first sale.
                    </div>
                </div>
                <Link to="/affiliate/links" className="pxl-gs-cta">
                    Get my links
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 6, verticalAlign: "middle" }}>
                        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/>
                    </svg>
                </Link>
            </div>
            <div className="pxl-gs-steps">
                <div className="pxl-gs-step">
                    <div className="pxl-gs-step-num">01</div>
                    <div className="pxl-gs-step-icon"><IconLinkIcon /></div>
                    <div className="pxl-gs-step-body">
                        <div className="pxl-gs-step-title">Copy your referral link</div>
                        <div className="pxl-gs-step-desc">
                            Go to the <Link to="/affiliate/links">Links tab</Link> and grab your personal URL. Every visitor who clicks it is attributed to you automatically.
                        </div>
                    </div>
                </div>
                <div className="pxl-gs-arrow" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                </div>
                <div className="pxl-gs-step">
                    <div className="pxl-gs-step-num">02</div>
                    <div className="pxl-gs-step-icon"><IconShare /></div>
                    <div className="pxl-gs-step-body">
                        <div className="pxl-gs-step-title">Share with your audience</div>
                        <div className="pxl-gs-step-desc">
                            Post on social media, include in your emails, or embed on your website. Clicks and conversions are tracked in real time.
                        </div>
                    </div>
                </div>
                <div className="pxl-gs-arrow" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                    </svg>
                </div>
                <div className="pxl-gs-step">
                    <div className="pxl-gs-step-num">03</div>
                    <div className="pxl-gs-step-icon"><IconDollar /></div>
                    <div className="pxl-gs-step-body">
                        <div className="pxl-gs-step-title">Watch your balance grow</div>
                        <div className="pxl-gs-step-desc">
                            When a referred visitor subscribes, commission is added to your balance. Request a payout once you hit the minimum threshold.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MiniChart({ daily }) {
    const [tooltip, setTooltip] = useState(null);
    const showTooltip = useCallback((e, d) => {
        const r = e.currentTarget.getBoundingClientRect();
        setTooltip({ x: r.left + r.width / 2, y: r.top, d });
    }, []);

    const maxClicks   = Math.max(1,    ...daily.map(d => d.clicks));
    const maxEarnings = Math.max(0.01, ...daily.map(d => d.earnings));
    const gridLines   = [0, 1, 2, 3, 4];

    return (
        <div className="pxl-panel">
            <div className="pxl-panel-header">
                30-day performance
                <Link to="/affiliate/analytics" className="pxl-panel-header-link">Full analytics</Link>
            </div>
            <div className="pxl-panel-body">
                <div className="pxl-analytics-chart-legend">
                    <span><i className="pxl-legend-dot pxl-legend-dot--clicks" /> Clicks</span>
                    <span><i className="pxl-legend-dot pxl-legend-dot--earnings" /> Earnings</span>
                </div>
                <div className="pxl-chart-inner" style={{ position: 'relative' }}>
                    <div className="pxl-chart-grid">
                        {gridLines.map(i => (
                            <div key={i} className={`pxl-chart-grid-line${i === gridLines.length - 1 ? ' pxl-chart-grid-line--base' : ''}`} />
                        ))}
                    </div>
                    <div className="pxl-analytics-chart">
                        {daily.map(d => (
                            <div className="pxl-analytics-chart-col" key={d.date}
                                onMouseEnter={e => showTooltip(e, d)}
                                onMouseLeave={() => setTooltip(null)}>
                                <div className="pxl-analytics-chart-bars">
                                    <div className="pxl-analytics-chart-bar pxl-analytics-chart-bar--clicks"
                                        style={{ height: `${(d.clicks / maxClicks) * 100}%` }} />
                                    <div className="pxl-analytics-chart-bar pxl-analytics-chart-bar--earnings"
                                        style={{ height: `${(Math.max(0, d.earnings) / maxEarnings) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="pxl-analytics-chart-divider" />
                    <div className="pxl-analytics-chart-axis">
                        {daily.length > 1 && <span>{shortDate(daily[0].date)}</span>}
                        <span>{shortDate(daily[daily.length - 1].date)}</span>
                    </div>
                </div>
            </div>
            {tooltip && (
                <div className="pxl-chart-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
                    <div className="pxl-chart-tooltip-date">{shortDate(tooltip.d.date)}</div>
                    <div className="pxl-chart-tooltip-row">
                        <span className="pxl-chart-tooltip-label"><span className="pxl-chart-tooltip-dot" style={{ background: '#7ea8e0' }} />Clicks</span>
                        <span className="pxl-chart-tooltip-val">{tooltip.d.clicks}</span>
                    </div>
                    <div className="pxl-chart-tooltip-row">
                        <span className="pxl-chart-tooltip-label"><span className="pxl-chart-tooltip-dot" style={{ background: '#2db67c' }} />Earnings</span>
                        <span className="pxl-chart-tooltip-val">${Number(tooltip.d.earnings).toFixed(2)}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtMoney(n) {
    if (n === null || n === undefined) return "—";
    return `$${Number(n).toFixed(2)}`;
}
function fmtNum(n) {
    if (n === null || n === undefined) return "—";
    return Number(n).toLocaleString();
}
function shortDate(iso) {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconTrendingUp() {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>;
}
function IconCreditCard() {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
}
function IconSend() {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
}
function IconUserCheck() {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/></svg>;
}
function IconMousePointer() {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/><path d="M13 13l6 6"/></svg>;
}
function IconCalendar() {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
}
function IconCopy() {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
}
function IconCheck() {
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
}
function IconRocket() {
    return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>;
}
function IconLinkIcon() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>;
}
function IconShare() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>;
}
function IconDollar() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
}
